import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/** Formularze trzymamy w modalach (konwencja UX BillVis). */
export function DialogContent({
  className,
  children,
  title,
  description,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & { title: string; description?: ReactNode }) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out" />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[min(32rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2',
          'max-h-[calc(100vh-4rem)] overflow-y-auto rounded-xl border border-border bg-card p-5 shadow-xl',
          className,
        )}
        {...props}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-base font-semibold tracking-tight">{title}</DialogPrimitive.Title>
            {description && (
              <DialogPrimitive.Description className="mt-1 text-sm text-muted-foreground">
                {description}
              </DialogPrimitive.Description>
            )}
          </div>
          <DialogPrimitive.Close
            aria-label="Zamknij"
            className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/** Błędy formularza w jednym miejscu, nad przyciskami. */
export function ModalError({ children }: { children?: ReactNode }) {
  if (!children) return null;
  return (
    <div
      role="alert"
      className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
    >
      {children}
    </div>
  );
}

export function FormGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('space-y-4', className)}>{children}</div>;
}

export function Field({
  label,
  htmlFor,
  required,
  error,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  required?: boolean;
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium leading-none text-foreground">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/**
 * STOPKA MODALA — PRZYKLEJONA DO DOŁU, więc „Anuluj/Zapisz" są widoczne CAŁY CZAS.
 *
 * ⚠️ ZGŁOSZONE Z UŻYTKOWANIA (2026-08-18): „przy edycji pracownika przyciski Anuluj/Zapisz były cały
 * czas widoczne — żeby nie trzeba było scrollować do samego dołu". Formularz pracownika ma kilkanaście
 * znaczników i sekcję powiązań, więc na niższym ekranie zapis wypadał poza widok: człowiek wypełniał
 * pole i nie miał czym zatwierdzić bez przewijania.
 *
 * ⚠️ `sticky`, NIE `fixed`. Kontener modala (`DialogContent`) jest tym, co się przewija, więc element
 * przyklejony do jego dołu zostaje w oknie modala — `fixed` przykleiłby pasek do okna PRZEGLĄDARKI
 * i wisiałby nad tłem także wtedy, gdy modal jest krótki.
 *
 * ⚠️ UJEMNE MARGINESY WYRÓWNUJĄ PASEK DO KRAWĘDZI. Zawartość modala ma `p-5`, więc bez `-mx-5 -mb-5`
 * tło paska kończyłoby się przed krawędzią i przez szparę przewijałby się tekst formularza.
 * `bg-card` jest obowiązkowe: bez niego treść przechodziłaby POD przyciskami i była nieczytelna.
 *
 * ⚠️ `-bottom-5`, NIE `bottom-0` — I TO JEST ZMIERZONE, nie przeczucie. Pierwsza wersja miała
 * `bottom-0` i użytkownik zgłosił: „pasek się wyświetla, ale nie na samym dole i jest kawałek,
 * który wyświetla się poniżej paska". Powód: przyklejenie liczy się do krawędzi obszaru
 * przewijania, a ten kończy się PRZED dolnym wypełnieniem kontenera — pod paskiem zostawało
 * okno wysokości `p-5` + obramowanie, przez które przejeżdżała przewijana treść. Odtworzone
 * na czterech wariantach w przeglądarce i policzone: `bottom-0` → 21 px okna, `-bottom-5` → 1 px
 * (samo obramowanie).
 *
 * ⚠️ DLACZEGO NIE ZDJĄĆ DOLNEGO WYPEŁNIENIA Z `DialogContent` (to też dawało 1 px): modale BEZ
 * stopki straciłyby wtedy odstęp pod treścią. Pasek naprawia sam siebie i nie zmienia niczego
 * u sąsiadów. Własne `pb-5` wraca odstęp, który zabrał ujemny margines.
 */
/**
 * ⚠️⚠️ `flex-wrap` JEST WARUNKIEM WIDOCZNOŚCI PRZYCISKÓW, nie kosmetyką — zgłoszone 09.09.2026:
 * „mamy na dole ucięte przyciski, zamknij jest poza ekranem". Modal ma `w-[min(32rem,…)]`, a przy
 * TRZECH przyciskach (u zgłaszającego: „Zamknij", „Zapisz plik", „Ustaw pozostałe tak samo —
 * wszystkie (2)") ich suma przekracza tę szerokość. Bez zawijania `justify-end` wypycha nadmiar
 * w LEWO, za krawędź modala — czyli przycisk zamknięcia wyjeżdża poza ekran i nie da się go kliknąć.
 *
 * ⚠️ ZAWIJANIE NIC NIE ZMIENIA, DOPÓKI PRZYCISKI SIĘ MIESZCZĄ, więc dotyka tylko tych stopek,
 * które faktycznie są za szerokie — a tę stopkę używa kilkadziesiąt miejsc w portalu.
 */
export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="sticky -bottom-5 -mx-5 -mb-5 mt-5 flex flex-wrap justify-end gap-2 border-t border-border bg-card px-5 pb-5 pt-3">
      {children}
    </div>
  );
}
